import i18next, { StringMap, TOptions, type i18n as i18nType } from "i18next";
import Backend from "i18next-chained-backend";
import HttpApi from "i18next-http-backend";
import LocalStorageBackend from "i18next-localstorage-backend";
import { useEffect, useState } from "react";
import { initReactI18next, useTranslation as useTranslationI18n } from "react-i18next";
import { buildTranslationsLoadPath } from "./helpers/buildTranslationsLoadPath.js";
import { computeCurrentLanguage } from "./helpers/computeCurrentLanguage.js";
import { AugmentedI18n, BaseTranslationDictionary, CustomTFunction, Languages } from "./types.js";
import { buildTranslations } from "./utils.js";

let initTranslations: BaseTranslationDictionary;

const augmentedI18n = Object.defineProperty(i18next, "translationsData", {
    value: {},
    writable: true,
}) as unknown as AugmentedI18n<any>;

const setupI18next = async (params: {
    fragment: string;
    localStoragePrefix: string;
    pathSuffix?: string;
    skipTranslationsBuild?: boolean;
}): Promise<void> => {
    const { fragment, localStoragePrefix, pathSuffix, skipTranslationsBuild = false } = params;

    try {
        await i18next
            .use(Backend as any)
            .use(initReactI18next)
            .init({
                supportedLngs: Object.values(Languages),
                returnNull: false,
                returnEmptyString: false,
                backend: {
                    backends: [LocalStorageBackend, HttpApi],
                    backendOptions: [
                        {
                            prefix: localStoragePrefix,
                            expirationTime: 60 * 60 * 1000,
                            store: typeof window !== "undefined" ? window.localStorage : undefined,
                        },
                        {
                            loadPath: buildTranslationsLoadPath({ fragment, pathSuffix }),
                            parse: (response: string | { data: any }) => {
                                try {
                                    if (typeof response === "string") {
                                        const res = JSON.parse(response);
                                        return res.data;
                                    }
                                    return response.data;
                                } catch (e) {
                                    console.error("Failed to parse i18n response", e);
                                    return {}; // fallback to empty object
                                }
                            },
                        },
                    ],
                },
                fallbackLng: Languages.EN,
                debug: false,
                react: {
                    useSuspense: false,
                },
                interpolation: {
                    escapeValue: false,
                },
            });
    } catch (e) {
        console.error("i18next initialization failed. Falling back to default config.", e);
        i18next.init({
            lng: Languages.EN,
            resources: {
                [Languages.EN]: {
                    translation: {},
                },
            },
            fallbackLng: Languages.EN,
            react: {
                useSuspense: false,
            },
        });
    }

    if (skipTranslationsBuild) {
        return;
    }

    initTranslations = buildTranslations(augmentedI18n);
};

export const useInitTranslation = (params: {
    fragment: string;
    localStoragePrefix: string;
    pathSuffix?: string;
    skipTranslationsBuild?: boolean;
}) => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setupI18next(params).finally(() => setLoading(false));
    }, [JSON.stringify(params)]);

    return { loading };
};

export const useTranslation = <TranslationDictionary extends BaseTranslationDictionary>(): {
    t: CustomTFunction;
    changeLanguage: (ln: Languages) => void;
    fetchLanguageData: (language: Languages) => Promise<{ hasFailed: false } | { hasFailed: true }>;
    currentLanguage: Languages;
    translations: TranslationDictionary;
    languages: Languages[];
    i18n: i18nType;
    isLoadingLanguage: boolean;
} => {
    const [translations, setTranslations] = useState<TranslationDictionary>(initTranslations as TranslationDictionary);
    const [isLoadingLanguage, setIsLoadingLanguage] = useState(false);

    const { t, i18n } = useTranslationI18n<"translation", string>();

    const changeLanguage = (ln: Languages): void => {
        i18n.changeLanguage(ln, () => setTranslations(buildTranslations<TranslationDictionary>(augmentedI18n)));
    };

    const wrappedT: CustomTFunction = (key, options) => {
        const res = typeof options === "string" ? t(key, options, { fallbackLng: Languages.EN }) : t(key, { ...options, fallbackLng: Languages.EN });
        if (res !== "") return res;

        return typeof options === "string" ? t(key, options, { lng: Languages.EN }) : t(key, { lng: Languages.EN, ...options });
    };

    const fetchLanguageData = async (language: Languages): Promise<{ hasFailed: false } | { hasFailed: true }> => {
        setIsLoadingLanguage(true);
        try {
            await i18n.loadLanguages(language);
            return { hasFailed: false };
        } catch (e) {
            return { hasFailed: true };
        } finally {
            setIsLoadingLanguage(false);
        }
    };

    const currentLanguage = computeCurrentLanguage(i18n.language);

    return {
        t: wrappedT,
        changeLanguage,
        currentLanguage,
        fetchLanguageData,
        translations,
        languages: Object.values(Languages),
        i18n,
        isLoadingLanguage,
    };
};

export const useTranslationV2 = <T extends string>(): {
    t: (key: T, options?: string | TOptions<StringMap>) => string;
    changeLanguage: (ln: Languages) => void;
    fetchLanguageData: (language: Languages) => Promise<{ hasFailed: false } | { hasFailed: true }>;
    currentLanguage: Languages;
    languages: Languages[];
    i18n: i18nType;
    isLoadingLanguage: boolean;
} => {
    const [isLoadingLanguage, setIsLoadingLanguage] = useState(false);

    const { t, i18n } = useTranslationI18n<"translation", string>();

    const changeLanguage = (ln: Languages): void => {
        i18n.changeLanguage(ln);
    };

    const wrappedT = (key: T, options?: string | TOptions<StringMap>) => {
        const res = typeof options === "string" ? t(key, options, { fallbackLng: Languages.EN }) : t(key, { ...options, fallbackLng: Languages.EN });
        if (res !== "") return res;

        return typeof options === "string" ? t(key, options, { lng: Languages.EN }) : t(key, { lng: Languages.EN, ...options });
    };

    const fetchLanguageData = async (language: Languages): Promise<{ hasFailed: false } | { hasFailed: true }> => {
        setIsLoadingLanguage(true);
        try {
            await i18n.loadLanguages(language);
            return { hasFailed: false };
        } catch (e) {
            return { hasFailed: true };
        } finally {
            setIsLoadingLanguage(false);
        }
    };

    const currentLanguage = computeCurrentLanguage(i18n.language);

    return {
        t: wrappedT,
        changeLanguage,
        currentLanguage,
        fetchLanguageData,
        languages: Object.values(Languages),
        i18n,
        isLoadingLanguage,
    };
};
